import { useMemo } from "react";

import {
  PortfolioAdditionalStatTile,
  PortfolioCardShell,
  PortfolioCardsList,
  PortfolioEmptyAdditionalStatTile,
  PortfolioMetricCard,
  PortfolioTrialHeader,
} from "@/views/Portfolio/components";
import type {
  PortfolioAdditionalStat,
  PortfolioMetricView,
  PortfolioSegment,
  PortfolioTrialHeaderInfo,
  TrialInfo,
} from "@/views/Portfolio/types";
import {
  buildPerSiteAdditionalStat,
  derivePortfolioMetricView,
  indexTrialsById,
  resolvePortfolioTrialHeader,
} from "@/views/Portfolio/utils";

type EarlyTermReasons = {
  ae: number;
  consent_withdrawn: number;
  lost_to_follow_up: number;
  all_other: number;
};

type RetentionRow = {
  trial_id: string;
  retention_rate: number;
  retention_rate_delta_30d?: number | null;
  early_termination_rate: number;
  early_termination_rate_delta_30d?: number | null;
  early_term_reasons: EarlyTermReasons;
  time_to_discontinuation: number;
  time_to_discontinuation_delta_30d?: number | null;
  comp_scheduled_visits: number;
  comp_scheduled_visits_delta_30d?: number | null;
  per_site_retention_rate_avg?: number | null;
  per_site_retention_rate_sd?: number | null;
  per_site_retention_rate_range_low?: number | null;
  per_site_retention_rate_range_high?: number | null;
  per_site_early_term_avg?: number | null;
  per_site_early_term_sd?: number | null;
  per_site_early_term_range_low?: number | null;
  per_site_early_term_range_high?: number | null;
  per_site_time_to_disc_avg?: number | null;
  per_site_time_to_disc_sd?: number | null;
  per_site_time_to_disc_range_low?: number | null;
  per_site_time_to_disc_range_high?: number | null;
  per_site_comp_visits_avg?: number | null;
  per_site_comp_visits_sd?: number | null;
  per_site_comp_visits_range_low?: number | null;
  per_site_comp_visits_range_high?: number | null;
};

const RETENTION_TIME_TO_DISCONTINUATION_UNIT = "weeks";

const RETENTION_ADDITIONAL_STATS = [
  { key: "retention-rate", unit: "%" },
  { key: "early-term", unit: "%" },
  { key: "breakdown", unit: undefined },
  { key: "time-to-disc", unit: RETENTION_TIME_TO_DISCONTINUATION_UNIT },
  { key: "comp-visits", unit: "%" },
] as const;

type RetentionCard = PortfolioTrialHeaderInfo & {
  retentionRateView: PortfolioMetricView;
  earlyTerminationRateView: PortfolioMetricView;
  earlyTermReasonsView: PortfolioMetricView;
  earlyTermReasonsSegments: PortfolioSegment[];
  timeToDiscontinuationView: PortfolioMetricView;
  compScheduledVisitsView: PortfolioMetricView;
  additionalStats: (PortfolioAdditionalStat | "breakdown")[];
  earlyTermReasonsBreakdown: EarlyTermReasons;
  initiallyExpanded: boolean;
};

function buildRetentionMetricView(
  actual: number,
  delta30d: number | null | undefined,
  isComplete: boolean,
): PortfolioMetricView {
  const rawDelta = isComplete ? null : (delta30d ?? null);
  return derivePortfolioMetricView({
    actual,
    planned: { value: null, date: null, frequency: null },
    total: null,
    delta30d: rawDelta === null ? null : Math.round(rawDelta),
    lifecycle: isComplete ? "final" : "active",
    planTracked: false,
  });
}

const REASON_COLORS: Record<keyof EarlyTermReasons, string> = {
  ae: "#C98AB2",
  consent_withdrawn: "#8C1D5B",
  lost_to_follow_up: "#C9CED6",
  all_other: "#E53935",
};

const BREAKDOWN_LABELS: { key: keyof EarlyTermReasons; label: string }[] = [
  { key: "ae", label: "AE" },
  { key: "consent_withdrawn", label: "Consent withdrawn" },
  { key: "lost_to_follow_up", label: "Lost to follow-up" },
  { key: "all_other", label: "All Other" },
];

function buildEarlyTermReasonsSegments(
  reasons: EarlyTermReasons,
): PortfolioSegment[] {
  return [
    { value: reasons.ae, color: REASON_COLORS.ae, label: "AE" },
    {
      value: reasons.consent_withdrawn,
      color: REASON_COLORS.consent_withdrawn,
      label: "Consent W/D",
    },
    {
      value: reasons.lost_to_follow_up + reasons.all_other,
      color: REASON_COLORS.lost_to_follow_up,
      label: "Other",
    },
  ];
}

function BreakdownStatTile({ reasons }: { reasons: EarlyTermReasons }) {
  return (
    <div className="flex h-full min-h-[76px] flex-col justify-start bg-[#fbfbfc] px-5 py-3">
      <span className="text-[10px] font-semibold uppercase tracking-[0.12em] text-[#8EA0BF]">
        Breakdown
      </span>
      <div className="mt-1 flex flex-col gap-[6px]">
        {BREAKDOWN_LABELS.map((item) => (
          <div
            key={item.key}
            className="flex items-center justify-between text-[12px] leading-none"
          >
            <div className="flex items-center gap-1.5">
              <span
                className="inline-block h-2 w-2 rounded-full"
                style={{ backgroundColor: REASON_COLORS[item.key] }}
              />
              <span className="text-[#556C75]">{item.label}</span>
            </div>
            <span className="font-semibold text-[#102033]">
              {reasons[item.key]}%
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RetentionCardView({ card }: { card: RetentionCard }) {
  return (
    <PortfolioCardShell
      header={
        <PortfolioTrialHeader
          trial={card}
          titleClassName="!font-bold"
          linkToTrial
        />
      }
      metricColumns={5}
      initiallyExpanded={card.initiallyExpanded}
      additionalContent={card.additionalStats.map((stat, index) => {
        if (stat === "breakdown") {
          return (
            <BreakdownStatTile
              key={`${card.trial_id}-breakdown`}
              reasons={card.earlyTermReasonsBreakdown}
            />
          );
        }
        if (!stat) {
          return (
            <PortfolioEmptyAdditionalStatTile
              key={`${card.trial_id}-empty-${RETENTION_ADDITIONAL_STATS[index].key}`}
            />
          );
        }
        return (
          <PortfolioAdditionalStatTile
            key={`${card.trial_id}-${RETENTION_ADDITIONAL_STATS[index].key}`}
            stat={stat}
            unit={RETENTION_ADDITIONAL_STATS[index].unit}
          />
        );
      })}
    >
      <PortfolioMetricCard
        label="RETENTION RATE"
        view={card.retentionRateView}
        suffix="%"
      />
      <PortfolioMetricCard
        label="EARLY TERMINATION RATE"
        view={card.earlyTerminationRateView}
        suffix="%"
        progressTone="gold"
        invertDelta
      />
      <PortfolioMetricCard
        label="EARLY TERM REASONS"
        view={card.earlyTermReasonsView}
        suffix="% Due to AE"
        segments={card.earlyTermReasonsSegments}
      />
      <PortfolioMetricCard
        label="TIME → DISCONTINUATION"
        view={card.timeToDiscontinuationView}
        suffix={RETENTION_TIME_TO_DISCONTINUATION_UNIT}
      />
      <PortfolioMetricCard
        label="COMP. SCHEDULED VISITS"
        view={card.compScheduledVisitsView}
        suffix="%"
      />
    </PortfolioCardShell>
  );
}

function buildRetentionCard(
  row: RetentionRow,
  trialsById: Map<string, TrialInfo>,
): RetentionCard {
  const trial = trialsById.get(row.trial_id);
  const header = resolvePortfolioTrialHeader(row.trial_id, trial);

  return {
    ...header,
    retentionRateView: buildRetentionMetricView(
      row.retention_rate,
      row.retention_rate_delta_30d,
      header.isComplete,
    ),
    earlyTerminationRateView: buildRetentionMetricView(
      row.early_termination_rate,
      row.early_termination_rate_delta_30d,
      header.isComplete,
    ),
    earlyTermReasonsView: buildRetentionMetricView(
      row.early_term_reasons.ae,
      null,
      header.isComplete,
    ),
    earlyTermReasonsSegments: buildEarlyTermReasonsSegments(
      row.early_term_reasons,
    ),
    timeToDiscontinuationView: buildRetentionMetricView(
      row.time_to_discontinuation,
      row.time_to_discontinuation_delta_30d,
      header.isComplete,
    ),
    compScheduledVisitsView: buildRetentionMetricView(
      row.comp_scheduled_visits,
      row.comp_scheduled_visits_delta_30d,
      header.isComplete,
    ),
    additionalStats: [
      buildPerSiteAdditionalStat(
        row.per_site_retention_rate_avg,
        row.per_site_retention_rate_sd,
        row.per_site_retention_rate_range_low,
        row.per_site_retention_rate_range_high,
        1,
      ),
      buildPerSiteAdditionalStat(
        row.per_site_early_term_avg,
        row.per_site_early_term_sd,
        row.per_site_early_term_range_low,
        row.per_site_early_term_range_high,
        1,
      ),
      "breakdown",
      buildPerSiteAdditionalStat(
        row.per_site_time_to_disc_avg,
        row.per_site_time_to_disc_sd,
        row.per_site_time_to_disc_range_low,
        row.per_site_time_to_disc_range_high,
        1,
      ),
      buildPerSiteAdditionalStat(
        row.per_site_comp_visits_avg,
        row.per_site_comp_visits_sd,
        row.per_site_comp_visits_range_low,
        row.per_site_comp_visits_range_high,
        1,
      ),
    ],
    earlyTermReasonsBreakdown: row.early_term_reasons,
    initiallyExpanded: false,
  };
}

const buildCards = (rows: RetentionRow[], trialsById: Map<string, TrialInfo>) =>
  rows.map((row) => buildRetentionCard(row, trialsById));

export function PortfolioRetentionTrendsTab({
  data,
  trials,
}: {
  data?: RetentionRow[];
  trials?: TrialInfo[];
}) {
  const cards = useMemo(() => {
    const rows = Array.isArray(data) ? data : [];
    const trialSource = trials ?? [];
    const trialsById = indexTrialsById(trialSource);
    return buildCards(rows, trialsById);
  }, [data, trials]);

  return (
    <PortfolioCardsList>
      {cards.map((card) => (
        <RetentionCardView key={card.trial_id} card={card} />
      ))}
    </PortfolioCardsList>
  );
}
